import styled from 'styled-components'
import { Button, InlineCopy } from 'components'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { getRelativeTime } from 'utils'
import { usePostQuery } from 'hooks'
import { Markdown } from './styles'

const CommentsSection = styled.div`
  width: 100%;
  margin-top: 1rem;
  border-top: 1px solid ${({ theme: { colors } }) => colors('chrome')};
  padding-top: 0.75rem;
`

const CommentItem = styled.div`
  padding: 0.5rem 0;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors('chrome', 0.5)};
  &:last-of-type { border-bottom: 0; }
`

const CommentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
`

const ReplyList = styled.div`
  margin-left: 1rem;
  border-left: 2px solid ${({ theme: { colors } }) => colors('chrome')};
  padding-left: 0.75rem;
  margin-top: 0.5rem;
`

const ReplyItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.35rem 0;
  gap: 0.5rem;
`

const ReplyLink = styled.span`
  font-size: 0.75rem;
  cursor: pointer;
  margin-top: 0.35rem;
  display: inline-block;
  ${({ theme: { colors } }) => `color: ${colors('link')};`}
`

const CommentInputRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`

const CommentInput = styled.input`
  flex: 1;
  border: 1px solid ${({ theme: { colors } }) => colors('chrome')};
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
  background: transparent;
  color: ${({ theme: { colors } }) => colors('text')};
  outline: none;
`

// Not part of the public `components` barrel — only ShowPosts imports this,
// same as before the split (it was module-private in the original Post.js).
export const PostComments = ({ post, onComment }) => {
  const [comment, setComment] = useState('')
  const [replyTo, setReplyTo] = useState(null)
  const [reply, setReply] = useState('')
  const { post: postComment } = usePostQuery({ url: `/posts/${post.id}/comments` })

  const submit = async () => {
    if (!comment.trim()) return
    await postComment({ comment, commentable_type: 'post', commentable_id: post.id })
    setComment('')
    onComment()
  }

  const submitReply = async (parentId) => {
    if (!reply.trim()) return
    await postComment({ comment: reply, commentable_type: 'post', commentable_id: post.id, parent_id: parentId })
    setReply('')
    setReplyTo(null)
    onComment()
  }

  return (
    <CommentsSection>
      {post.comments?.map((c) => (
        <CommentItem key={c.id}>
          <CommentRow>
            {c.username && <InlineCopy size="0.75rem">{c.username}</InlineCopy>}
            <InlineCopy size="0.75rem">{getRelativeTime(c.created_at)}</InlineCopy>
          </CommentRow>
          <Markdown style={{ fontSize: '0.85rem' }}><ReactMarkdown>{c.comment}</ReactMarkdown></Markdown>
          {c.replies?.length > 0 && (
            <ReplyList>
              {c.replies.map((r, i) => (
                <ReplyItem key={i}>
                  <div>
                    <ReplyItem>
                      {r.username && <InlineCopy size="0.7rem">{r.username}</InlineCopy>}
                      <InlineCopy size="0.7rem">{getRelativeTime(r.created_at)}</InlineCopy>
                    </ReplyItem>
                    <Markdown style={{ fontSize: '0.8rem' }}><ReactMarkdown>{r.comment}</ReactMarkdown></Markdown>
                  </div>
                </ReplyItem>
              ))}
            </ReplyList>
          )}
          <ReplyLink onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}>
            reply
          </ReplyLink>
          {replyTo === c.id && (
            <CommentInputRow>
              <CommentInput
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitReply(c.id)}
                placeholder={`Reply to ${c.username}…`}
                autoFocus
              />
              <Button onPress={() => submitReply(c.id)}>Post</Button>
            </CommentInputRow>
          )}
        </CommentItem>
      ))}
      <CommentInputRow>
        <CommentInput
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Add a comment…"
        />
        <Button onPress={submit}>Post</Button>
      </CommentInputRow>
    </CommentsSection>
  )
}
